import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center text-red-600 hover:text-red-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Terms of Use</h1>
        <p className="text-gray-600 mb-8">Last updated: 18th January 2026</p>
        
        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Welcome to 30 Day Skills ("we", "our", "us"). These Terms of Use govern your access to and use of our website at 30dayskills.co.uk (the "Site"). By accessing or using the Site, you agree to be bound by these Terms. If you do not agree, please do not use the Site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Intellectual Property Rights</h2>
            <p className="text-gray-700 mb-4">
              All content on this Site, including but not limited to text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, is the property of 30 Day Skills or its content suppliers and is protected by United Kingdom and international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-700 mb-4">
              You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Site without our prior written consent, except as follows:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Your computer may temporarily store copies of such materials in RAM incidental to your accessing and viewing those materials.</li>
              <li>You may store files that are automatically cached by your web browser for display enhancement purposes.</li>
              <li>You may print or download one copy of a reasonable number of pages of the Site for your own personal, non-commercial use and not for further reproduction, publication, or distribution.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree not to use the Site:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>In any way that violates any applicable local, national, or international law or regulation.</li>
              <li>To transmit, or procure the sending of, any advertising or promotional material without our prior written consent.</li>
              <li>To impersonate or attempt to impersonate the company, a company employee, another user, or any other person or entity.</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Site.</li>
              <li>To attempt to gain unauthorised access to any portion of the Site, other accounts, computer systems, or networks connected to the Site.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Disclaimers</h2>
            <p className="text-gray-700 mb-4">
              The information provided on this Site is for general informational and educational purposes only. While we strive to provide accurate and up-to-date information, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the Site or the information, products, services, or related graphics contained on the Site.
            </p>
            <p className="text-gray-700 mb-4">
              The Site and its content are provided on an "as is" and "as available" basis. We do not warrant that the Site will be uninterrupted, timely, secure, or error-free.
            </p>
            <p className="text-gray-700 mb-4">
              Any reliance you place on information from this Site is strictly at your own risk. The content is not intended to be a substitute for professional advice. Always seek the advice of qualified professionals with any questions you may have.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the fullest extent permitted by applicable law, in no event shall 30 Day Skills, its affiliates, directors, employees, agents, or licensors be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Your access to or use of (or inability to access or use) the Site.</li>
              <li>Any conduct or content of any third party on the Site.</li>
              <li>Any content obtained from the Site.</li>
              <li>Unauthorised access, use, or alteration of your transmissions or content.</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Our total liability to you for all claims arising from or relating to these Terms or the Site shall not exceed the amount you paid us, if any, in the twelve (12) months prior to the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Third-Party Links</h2>
            <p className="text-gray-700 mb-4">
              The Site may contain links to third-party websites or services that are not owned or controlled by 30 Day Skills. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. We do not warrant the offerings of any of these entities or their websites.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-700 mb-4">
              By continuing to access or use our Site after any revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms of Use, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> legal@30dayskills.co.uk<br />
              <strong>Website:</strong> 30dayskills.co.uk
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
